import React, { useEffect, useRef, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { VatReturnDA } from '../../services/admin/vat.return.service';
import { VatCaseVoucher } from '../../models/vat.return.model';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide, modalShow } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppModalAttachDocument from './ModalAttachDocument';

export interface AppModalVatAdjustmentEditProps {
  idCompany: string;
  idPeriod: string;
  caseCode: string;
  label: string;
  currentAmount: number;
  currentNote: string;
  currentVouchers: VatCaseVoucher[];
  isFiled: boolean;
  onSaved: () => void;
};

let errorResponse: Error;

const MODAL_ID = 'modal-vat-adjustment-edit';
const MODAL_ATTACH_ID = 'modal-vat-adj-attach-document';

type PendingAttachment = {
  type: 'file';
  file: File;
  id_document_inbox?: undefined;
  name?: undefined;
} | {
  type: 'existing';
  file?: undefined;
  id_document_inbox: string;
  name: string;
}

const AppModalVatAdjustmentEdit: React.FunctionComponent<AppModalVatAdjustmentEditProps> = ({idCompany, idPeriod, caseCode, label, currentAmount, currentNote, currentVouchers, isFiled, onSaved}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [existingVouchers, setExistingVouchers] = useState<VatCaseVoucher[]>([])
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setAmount(currentAmount !== 0 ? String(currentAmount) : '')
    setNote(currentNote)
    setExistingVouchers(currentVouchers ?? [])
    setPendingAttachments([])
    setUploadErrors([])
  }, [currentAmount, currentNote, currentVouchers, caseCode])

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(event.target.value)
  }

  const handleChangeNote = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value)
  }

  const handleAddFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles: PendingAttachment[] = Array.from(event.target.files).map(f => ({ type: 'file' as const, file: f }))
      setPendingAttachments(prev => [...prev, ...newFiles])
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePickExistingDocument = (id_document_inbox: string, doc?: { name: string }) => {
    setPendingAttachments(prev => [...prev, { type: 'existing' as const, id_document_inbox, name: doc?.name ?? id_document_inbox }])
  }

  const handleRemovePending = (index: number) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleViewVoucher = async (index: number) => {
    try {
      const response = await VatReturnDA.getAdjustmentVoucherUrl(idCompany, idPeriod, caseCode, index)
      if (response.status === 200 && response.data?.url) {
        window.open(response.data.url, '_blank')
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const handleDeleteVoucher = async (index: number, name: string) => {
    if (isFiled) return

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmDeleteVoucher,
      text: name,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.delete,
      cancelButtonText: lang.labels.cancel,
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' }
    })

    if (!result.isConfirmed) return

    try {
      const response = await VatReturnDA.deleteAdjustmentVoucher(idCompany, idPeriod, caseCode, index)
      if (response.status === 200 || response.status === 204) {
        setExistingVouchers(prev => prev.filter((_, i) => i !== index))
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitSave = async () => {
    if (isFiled) return
    setLoadIndicator('on')
    setUploadErrors([])

    const parsedAmount = parseFloat(amount) || 0

    try {
      // Step 1: upsert amount/note
      const response = await VatReturnDA.upsertAdjustment(idCompany, idPeriod, caseCode, parsedAmount, note || undefined)
      if (response.status !== 200 && response.status !== 201) {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
        setLoadIndicator('off')
        return
      }

      // Step 2: upload/attach pending items sequentially
      const errors: string[] = []
      for (const attachment of pendingAttachments) {
        try {
          let uploadResp
          if (attachment.type === 'file') {
            uploadResp = await VatReturnDA.attachAdjustmentVoucher(idCompany, idPeriod, caseCode, attachment.file)
          } else {
            uploadResp = await VatReturnDA.attachAdjustmentExistingDocument(idCompany, idPeriod, caseCode, attachment.id_document_inbox)
          }
          if (uploadResp.status !== 200 && uploadResp.status !== 201) {
            const name = attachment.type === 'file' ? attachment.file.name : attachment.name
            errors.push(`${name}: ${lang.labels.uploadFailed}`)
          }
        } catch {
          const name = attachment.type === 'file' ? attachment.file.name : attachment.name
          errors.push(`${name}: ${lang.labels.uploadFailed}`)
        }
      }

      if (errors.length > 0) {
        setUploadErrors(errors)
        setPendingAttachments([])
        setLoadIndicator('off')
        // Partial success: refresh parent to pick up the upsert + successful uploads
        onSaved()
        return
      }

      executeHide()
      onSaved()
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoadIndicator('off')
    }
  }

  const submitDelete = async () => {
    if (isFiled) return
    setLoadIndicator('on')

    try {
      const response = await VatReturnDA.deleteAdjustment(idCompany, idPeriod, caseCode)
      if (response.status === 200 || response.status === 204) {
        executeHide()
        onSaved()
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoadIndicator('off')
    }
  }

  const executeHide = () => {
    modalHide(MODAL_ID)
    setTimeout(() => {
      setAmount('')
      setNote('')
      setExistingVouchers([])
      setPendingAttachments([])
      setUploadErrors([])
    }, 200)
  }

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const pendingDisplayName = (attachment: PendingAttachment): string => {
    return attachment.type === 'file' ? attachment.file.name : attachment.name
  }

  return (
    <>
      <div id={MODAL_ID} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="text-capitalize">{lang.labels.editVatAdjustment}</h2>
            </div>
            <div className="modal-body">
              <div className="form">
                <div className="mb-3">
                  <span className="badge badge-light fw-bold fs-7">{caseCode}</span>
                  <span className="fw-bold ms-2">{label}</span>
                </div>

                { isFiled &&
                  <div className="alert alert-warning d-flex align-items-center py-3 mb-4">
                    <span className="svg-icon svg-icon-2 svg-icon-warning me-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path opacity="0.3" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="black"/>
                        <path d="M11 7H13V13H11V7ZM11 15H13V17H11V15Z" fill="black"/>
                      </svg>
                    </span>
                    <span className="fw-bold fs-7">{lang.labels.periodIsFiledCannotEdit}</span>
                  </div>
                }

                <div className="mb-4">
                  <label className="form-label required fw-bold">{lang.labels.amount} (CHF)</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    name="amount"
                    value={amount}
                    onChange={handleChangeAmount}
                    disabled={isFiled}
                    placeholder="0.00"
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">{lang.labels.noteOptional}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    name="note"
                    value={note}
                    onChange={handleChangeNote}
                    disabled={isFiled}
                  />
                </div>

                <div className="mb-0">
                  <label className="form-label fw-bold">{lang.labels.addVouchers}</label>

                  { existingVouchers.length > 0 &&
                    <div className="mb-3">
                      { existingVouchers.map((v, i) => (
                        <div key={i} className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
                          <div className="d-flex align-items-center text-truncate me-2">
                            <i className="bi bi-paperclip me-2 text-muted"></i>
                            <span className="text-truncate fs-7 fw-semibold">{v.original_name}</span>
                            { v.size &&
                              <span className="text-muted fs-8 ms-2">({formatFileSize(v.size)})</span>
                            }
                          </div>
                          <div className="d-flex">
                            <button type="button" className="btn btn-sm btn-icon btn-light-primary me-1" title={lang.labels.view} onClick={() => handleViewVoucher(i)}>
                              <i className="bi bi-eye"></i>
                            </button>
                            { !isFiled &&
                              <button type="button" className="btn btn-sm btn-icon btn-light-danger" title={lang.labels.delete} onClick={() => handleDeleteVoucher(i, v.original_name)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  }

                  { pendingAttachments.length > 0 &&
                    <div className="mb-3">
                      { pendingAttachments.map((a, i) => (
                        <div key={i} className="d-flex align-items-center justify-content-between border border-dashed border-primary rounded px-3 py-2 mb-2">
                          <div className="d-flex align-items-center text-truncate me-2">
                            <i className={`me-2 ${a.type === 'file' ? 'bi bi-cloud-arrow-up text-primary' : 'bi bi-link-45deg text-info'}`}></i>
                            <span className="text-truncate fs-7">{pendingDisplayName(a)}</span>
                            <span className={`badge ms-2 fs-9 ${a.type === 'file' ? 'badge-light-primary' : 'badge-light-info'}`}>{lang.labels.pendingUpload}</span>
                          </div>
                          <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => handleRemovePending(i)}>
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  }

                  { uploadErrors.length > 0 &&
                    <div className="alert alert-danger py-2 mb-3">
                      { uploadErrors.map((err, i) => (
                        <div key={i} className="fs-7">{err}</div>
                      ))}
                    </div>
                  }

                  { !isFiled &&
                    <div className="d-flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="d-none"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleAddFiles}
                      />
                      <button type="button" className="btn btn-sm btn-light-primary" onClick={() => fileInputRef.current?.click()}>
                        <i className="bi bi-plus me-1"></i>
                        {lang.labels.addVoucher}
                      </button>
                      <button type="button" className="btn btn-sm btn-light-info" onClick={() => modalShow(MODAL_ATTACH_ID)}>
                        <i className="bi bi-link-45deg me-1"></i>
                        {lang.labels.selectExistingDocument}
                      </button>
                    </div>
                  }
                </div>
              </div>
            </div>
            <div className="modal-footer flex-center">
              <button className="btn btn-secondary mx-2" type="reset" onClick={executeHide}>{lang.labels.cancel}</button>
              { currentAmount !== 0 &&
                <button className="btn btn-danger mx-2" type="button" disabled={isFiled} data-kt-indicator={loadIndicator} onClick={submitDelete}>
                  <span className="indicator-label">{lang.labels.delete}</span>
                  <span className="indicator-progress">
                    <span className="spinner-border spinner-border-sm align-middle"></span>
                  </span>
                </button>
              }
              <button className="btn btn-primary mx-2" type="button" disabled={isFiled} data-kt-indicator={loadIndicator} onClick={submitSave}>
                <span className="indicator-label">{lang.labels.saveChanges}</span>
                <span className="indicator-progress">
                  <span className="spinner-border spinner-border-sm align-middle"></span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <AppModalAttachDocument
        id_company={idCompany}
        entity_type="accounting-entry"
        onAttached={() => {}}
        onPick={handlePickExistingDocument}
        modalId={MODAL_ATTACH_ID}
      />
    </>
  )
};

export default AppModalVatAdjustmentEdit;
